// src/review/canvas/sections/RiskEvaluationSection.jsx
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Card, Stack, Text, Grid, Badge, Group, Divider } from '@mantine/core';
import { useDispatch } from 'react-redux';
import EditableField from '../shared/EditableField';
import EditableSelectField from '../shared/EditableSelectField';
import { persistRiskRationale, persistRiskPriority } from '../../../redux/slices/risksSlice';
import { useReview } from '../../context/ReviewContext';

/**
 * RiskEvaluationSection - Shows risk evaluation criteria and scores
 * Pulls data from risk.priority, risk.rationale, and risk.evaluationData
 * Matches the data structure used by RiskEvaluationForm
 */
function RiskEvaluationSection({ risk }) {
  const dispatch = useDispatch();
  const { modelId, assessmentUuid } = useReview();

  // Get priority (overall risk level) and rationale
  const priority = risk.priority;
  const rationale = risk.rationale;

  // Save handler for priority
  const handleSavePriority = useCallback(async (newValue, oldValue) => {
    await dispatch(persistRiskPriority({
      modelId,
      assessmentUuid,
      riskId: risk.riskId,
      riskName: risk.riskName,
      sectionName: risk.areaName,
      category: risk.category,
      priority: newValue,
      rationale: risk.rationale,
    }));
  }, [dispatch, modelId, assessmentUuid, risk.riskId, risk.riskName, risk.areaName, risk.category, risk.rationale]);

  // Save handler for rationale
  const handleSaveRationale = useCallback(async (newValue, oldValue) => {
    await dispatch(persistRiskRationale({
      modelId,
      assessmentUuid,
      riskId: risk.riskId,
      riskName: risk.riskName,
      category: risk.category,
      rationale: newValue
    }));
  }, [dispatch, modelId, assessmentUuid, risk.riskId, risk.riskName, risk.category]);

  // Get detailed evaluation data (nested structure: themeId -> attribute -> criterionName -> value)
  const evaluationData = risk.evaluationData || {};

  // Priority options for dropdown
  const priorityOptions = [
    'Critical',
    'High',
    'Significant',
    'Moderate',
    'Low',
    'Negligible'
  ];

  // Map priority to color (matching SectionAssessmentCardV2)
  const getPriorityColor = (priority) => {
    if (!priority) return 'gray';
    const p = priority.toLowerCase();
    if (p === 'critical') return 'dark';
    if (p === 'high') return 'red';
    if (p === 'significant') return 'orange';
    if (p === 'moderate') return 'yellow';
    if (p === 'low') return 'green';
    if (p === 'negligible') return 'teal';
    return 'gray';
  };

  // Helper to format field names (camelCase to Title Case)
  const formatFieldName = (name) => {
    return name
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  // Check if we have evaluation data
  const hasEvaluationData = Object.keys(evaluationData).length > 0;

  return (
    <Card withBorder p="md">
      <Stack gap="md">
        <Text size="md" fw={700}>Risk Evaluation</Text>

        {priority && (
          <EditableSelectField
            label="Priority"
            value={priority}
            itemType="risk"
            itemId={risk.riskId}
            fieldName="priority"
            options={priorityOptions}
            placeholder="Select priority..."
            onSave={handleSavePriority}
            getColor={getPriorityColor}
          />
        )}

        {priority && rationale && (
          <EditableField
            label="Priority Rationale"
            value={rationale}
            itemType="risk"
            itemId={risk.riskId}
            fieldName="rationale"
            multiline
            minRows={3}
            placeholder="Explain the priority assessment..."
            onSave={handleSaveRationale}
          />
        )}

        {hasEvaluationData && (
          <>
            <Divider />
            <Text size="sm" fw={600} c="dimmed">Detailed Evaluation Criteria</Text>

            {/* Iterate through themes */}
            {Object.entries(evaluationData).map(([themeId, attributes]) => (
              <Card key={themeId} withBorder p="sm" bg="gray.0">
                <Stack gap="md">
                  {/* Iterate through attributes (severity, likelihood, etc.) */}
                  {Object.entries(attributes).map(([attribute, criteria]) => (
                    <Stack key={attribute} gap="xs">
                      <Text size="sm" fw={600} tt="capitalize" c="blue">
                        {formatFieldName(attribute)}
                      </Text>

                      <Grid gutter="xs">
                        {/* Iterate through criteria (base_severity, magnitude_of_harm, etc.) */}
                        {Object.entries(criteria).map(([criterionName, value]) => (
                          <Grid.Col key={criterionName} span={{ base: 12, md: 6 }}>
                            <Group gap="xs" align="center">
                              <Text size="xs" c="dimmed" style={{ flex: 1 }}>
                                {formatFieldName(criterionName)}:
                              </Text>
                              <Badge
                                size="sm"
                                variant="light"
                                color={
                                  value?.toLowerCase().includes('very high') || value?.toLowerCase().includes('critical') ? 'red' :
                                  value?.toLowerCase().includes('high') ? 'orange' :
                                  value?.toLowerCase().includes('medium') || value?.toLowerCase().includes('moderate') ? 'yellow' :
                                  value?.toLowerCase().includes('low') ? 'green' :
                                  'gray'
                                }
                              >
                                {value || 'Not assessed'}
                              </Badge>
                            </Group>
                          </Grid.Col>
                        ))}
                      </Grid>
                    </Stack>
                  ))}
                </Stack>
              </Card>
            ))}
          </>
        )}

        {!priority && !hasEvaluationData && (
          <Text size="sm" c="dimmed">
            No risk evaluation available. Complete the risk evaluation step.
          </Text>
        )}
      </Stack>
    </Card>
  );
}

RiskEvaluationSection.propTypes = {
  risk: PropTypes.shape({
    riskId: PropTypes.string.isRequired,
    riskName: PropTypes.string,
    priority: PropTypes.string,
    rationale: PropTypes.string,
    evaluationData: PropTypes.object,
    areaName: PropTypes.string,
    category: PropTypes.string,
  }).isRequired,
};

export default RiskEvaluationSection;
